import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { downloadFile } from "../lib/download";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { DownloadSimple, UploadSimple, CheckCircle, Warning, XCircle, MagicWand } from "@phosphor-icons/react";
import { formatUK } from "../lib/ukdate";

const STATUS_STYLE = { superseded: "bg-slate-200 text-slate-600", issued: "bg-emerald-50 text-emerald-800", under_review: "bg-amber-50 text-amber-800", other: "bg-blue-50 text-blue-800" };

export default function PolicyImport() {
  const [csv, setCsv] = useState("");
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null); // {delimiter, mapping, unmapped_headers}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => { const f = e.target.files?.[0]; if (f) setCsv(await f.text()); };
  const preview = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/policies/import/preview", { csv_text: csv });
      setRows(r.data.rows); setSummary(r.data.summary);
      setMeta({ delimiter: r.data.delimiter, mapping: r.data.mapping, unmapped_headers: r.data.unmapped_headers });
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const setAction = (i, action) => setRows(rows.map((r, j) => (j === i ? { ...r, action } : r)));
  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/policies/import/commit", { rows });
      setResult(r.data);
      toast.success(`Created ${r.data.created.length}, +${r.data.revisioned.length} revisions, skipped ${r.data.skipped.length}, failed ${r.data.failed?.length || 0}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="policy-import">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
        Imports create <b>unassigned drafts</b> — nothing is published, assigned or sent to staff, and a historical “Issued” source status never auto-publishes. Dates read as <b>DD/MM/YYYY</b>. Headers are auto-recognised (BOM, case and spacing ignored) and the delimiter is detected. Duplicates (matching reference) are flagged so you can skip, add as a draft revision, or create anew. Signed history is never overwritten.
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="dl-template" variant="outline" className="gap-1" onClick={() => downloadFile("/policies/import/template.csv", "policy_import_template.csv")}><DownloadSimple size={15} /> Download CSV template</Button>
        <label className="h-9 px-3 flex items-center gap-1 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50"><UploadSimple size={15} /> Upload CSV<input data-testid="csv-file" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} /></label>
      </div>
      <Textarea data-testid="csv-text" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="…or paste CSV content here" className="min-h-[110px] font-mono text-xs" />
      <div><Button data-testid="csv-preview-btn" disabled={busy || !csv.trim()} onClick={preview} className="bg-slate-800 hover:bg-slate-900">{busy ? "Validating…" : "Validate & preview"}</Button></div>

      {meta && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs" data-testid="import-mapping">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span className="text-slate-500">Delimiter: <b className="font-mono text-slate-800">{meta.delimiter}</b></span>
            {meta.unmapped_headers?.length > 0 && <span className="text-amber-700">Unmapped: {meta.unmapped_headers.join(", ")}</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(meta.mapping).map(([src, dst]) => (
              <span key={src} className={`rounded px-1.5 py-0.5 border ${dst ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-400"}`}>
                {src} → <b>{dst || "ignored"}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      {summary && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="import-preview">
          <div className="flex flex-wrap gap-4 text-sm mb-3">
            <span className="text-slate-600">Total: <b>{summary.total}</b></span>
            <span className="text-emerald-700">Valid: <b>{summary.valid}</b></span>
            <span className="text-red-600">Errors: <b>{summary.errors}</b></span>
            <span className="text-amber-700">Duplicates: <b>{summary.duplicates}</b></span>
            <span className="text-slate-500">Superseded: <b>{summary.superseded}</b></span>
            <span className="text-blue-700">Need repair: <b>{summary.needs_repair}</b></span>
          </div>
          <div className="flex flex-col gap-1.5 max-h-[420px] overflow-y-auto">
            {rows.map((r, i) => (
              <div key={i} data-testid={`import-row-${r.row}`} className="rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  {r.errors.length ? <XCircle size={16} className="text-red-600" /> : <CheckCircle size={16} className="text-emerald-600" />}
                  <span className="font-medium text-slate-800 truncate max-w-[240px]">{r.title || "(no title)"}</span>
                  <span className="text-[11px] font-mono text-slate-400">{r.reference} · rev {r.revision}</span>
                  {r.source_status && <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${STATUS_STYLE[r.source_status_normalised] || "bg-slate-100 text-slate-500"}`}>{r.source_status}</span>}
                  {r.issue_date && <span className="text-[11px] text-slate-500">issued {formatUK(r.issue_date)}</span>}
                  {r.last_reviewed_date && <span className="text-[11px] text-slate-500">reviewed {formatUK(r.last_reviewed_date)}</span>}
                  <span className="text-[10px] uppercase rounded px-1.5 py-0.5 bg-slate-100 text-slate-500">{r.content_format}</span>
                  {r.needs_format_repair && <span className="text-[10px] font-bold uppercase rounded px-1.5 py-0.5 bg-blue-50 text-blue-700 inline-flex items-center gap-1"><MagicWand size={11} />repair on open</span>}
                  <div className="flex-1" />
                  {(r.duplicate_of) && (
                    <select data-testid={`import-action-${r.row}`} value={r.action} onChange={(e) => setAction(i, e.target.value)} className="h-7 rounded border border-slate-200 text-xs px-1">
                      <option value="add_revision">Add draft revision to existing</option>
                      <option value="create">Create separate document</option>
                      <option value="skip">Skip</option>
                    </select>
                  )}
                </div>
                {r.duplicate_of && <div className="text-[11px] text-amber-700 mt-1">Duplicate of “{r.duplicate_of.title}” (ref {r.duplicate_of.ref}){r.conflict ? ` — ${r.conflict}` : ""}</div>}
                {r.classification_flag && <div className="text-[11px] text-purple-700 mt-1 flex items-center gap-1"><Warning size={11} /> {r.classification_flag}</div>}
                {r.errors.map((er, k) => <div key={k} className="text-[11px] text-red-600 mt-0.5 flex items-center gap-1"><XCircle size={11} /> <b>{er.column}</b>: {er.reason}</div>)}
                {r.warnings.filter((w) => w !== r.classification_flag).map((w, k) => <div key={k} className="text-[11px] text-amber-700 mt-0.5 flex items-center gap-1"><Warning size={11} /> {w}</div>)}
              </div>
            ))}
          </div>
          <Button data-testid="csv-commit-btn" disabled={busy || summary.valid === 0} onClick={commit} className="mt-3 bg-emerald-700 hover:bg-emerald-800">Import {summary.valid} valid row(s) as drafts</Button>
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="import-result">
          <b>Done.</b> Created {result.created.length} draft(s), added {result.revisioned.length} revision(s), skipped {result.skipped.length}, failed {result.failed?.length || 0}. New documents are unassigned drafts in Authoring; open one to repair its formatting and edit fields before publishing.
          {result.failed?.length > 0 && <div className="mt-1 text-red-700">Failed rows: {result.failed.map((f) => `${f.row} (${f.reason})`).join("; ")} — fix and re-import; successful rows are not duplicated.</div>}
        </div>
      )}
    </div>
  );
}
